import { getTypedObjectKeys, PayableFeeStatus } from '@schooly/api';
import { usePrevious } from '@schooly/hooks/use-previous';
import { graphic } from 'echarts';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { HSBColor } from './chartBar/utils';

export type ChartSeries<T> = {
  name: string;
  type: 'bar';
  emphasis?: {
    focus: 'series';
  };
  itemStyle?: {
    borderRadius?: [number, number, number, number];
    color?: string | ((params: { dataIndex: number }) => string) | graphic.LinearGradient;
    opacity?: number;
  };
  silent?: boolean;
  barGap?: string | number;
  barWidth?: number | string;
  data: T[];
};
export const CHARTS_SEARCH_PARAM = 'charts';
export const ARRANGE_BY_SEARCH_PARAM = 'arrange_by';
export const ZEROES_SEARCH_PARAM = 'zeroes';

export const useChartOpenFromSearchParams = () => {
  const [searchParams] = useSearchParams();
  const chartsQueryParamValue = searchParams.get(CHARTS_SEARCH_PARAM);
  const arrangeByQueryParamValue = searchParams.get(ARRANGE_BY_SEARCH_PARAM);

  return (
    chartsQueryParamValue === 'true' ||
    Boolean(arrangeByQueryParamValue && chartsQueryParamValue == null)
  );
};

export const useChartZeroesFromSearchParams = () => {
  const [searchParams] = useSearchParams();
  const zeroesQueryParamValue = searchParams.get(ZEROES_SEARCH_PARAM);

  return zeroesQueryParamValue === 'true' || zeroesQueryParamValue == null;
};

const payableFeesStatusColorMap: Record<PayableFeeStatus, HSBColor> = {
  paid: {
    h: 145,
    s: 46,
    b: 81,
  },
  due: {
    h: 28,
    s: 69,
    b: 95,
  },
  unpaid: {
    h: 28,
    s: 69,
    b: 95,
  },
  overdue: {
    h: 0,
    s: 63,
    b: 92,
  },
  projected: {
    h: 240,
    s: 60,
    b: 40,
  },
  upcoming: {
    h: 240,
    s: 60,
    b: 40,
  },
  voided: {
    h: 220,
    s: 12,
    b: 80,
  },
  cancelled: {
    h: 220,
    s: 12,
    b: 80,
  },
  partially_paid: {
    h: 14,
    s: 67,
    b: 97,
  },
};

interface PayableFeesStatusColor {
  key: PayableFeeStatus;
  color: HSBColor;
}

export const payableFeesStatusColorPalette: PayableFeesStatusColor[] = getTypedObjectKeys(
  payableFeesStatusColorMap,
).map((key) => ({ key, color: payableFeesStatusColorMap[key] }));

export const numberFormatter = (value: number) => new Intl.NumberFormat('en-US').format(value);

export const payableFeesSeriesLabelFormatter = (params: any) => {
  return numberFormatter(params.value as number);
};

export const payableFeesAxisValueFormatter = (value: number) => {
  if (value >= 1000000) return `${value / 1000000}M`;
  if (value >= 1000) return `${value / 1000}k`;
  return value;
};

type UseChartsToggleProps = {
  hasArrangeBy: boolean;
  isChartsOpened: boolean;
  onToggleChartsOpened: () => void;
};

export const useChartsToggle = ({
  hasArrangeBy,
  isChartsOpened,
  onToggleChartsOpened,
}: UseChartsToggleProps) => {
  const prevHasArrangeBy = usePrevious(hasArrangeBy);

  const arrangeBySelected = Boolean(!prevHasArrangeBy && hasArrangeBy);
  const arrangeByRemoved = Boolean(prevHasArrangeBy && !hasArrangeBy);

  const shouldOpenCharts = arrangeBySelected && !isChartsOpened;
  const shouldCloseCharts = arrangeByRemoved && isChartsOpened;

  const shouldToggleCharts = shouldOpenCharts || shouldCloseCharts;

  useEffect(() => {
    if (shouldToggleCharts) {
      onToggleChartsOpened();
    }
  }, [onToggleChartsOpened, shouldToggleCharts]);
};

export const SPACER_PREFIX = 'spacer-';

export function createSpacerSeries<T>(
  name: string,
  barWidth: number,
  rowCount: number,
): ChartSeries<T> {
  return {
    name: `${SPACER_PREFIX}${name}`,
    type: 'bar',
    silent: true,
    barWidth,
    itemStyle: { color: 'transparent' },
    data: Array(rowCount).fill({ name: '', value: null }),
  };
}

export function isSpacerSeries(seriesName: string): boolean {
  return seriesName.startsWith(SPACER_PREFIX);
}

function generatePlaceholderValue(index: number): number {
  const base = 0;
  const step = 30;
  const max = 120;
  const rangeCount = Math.floor((max - base) / step) + 1;
  return base + (index % rangeCount) * step;
}

type PlaceholderChartSeriesProps = {
  categories: string[];
  barsCount: number;
  opacity: number;
  itemStyle?: ChartSeries<any>['itemStyle'];
  barWidth?: number | string;
};

export function createPlaceholderChartSeries({
  categories,
  barsCount,
  opacity,
  itemStyle,
  barWidth,
}: PlaceholderChartSeriesProps): ChartSeries<{ name: string; value: number }>[] {
  const color = new graphic.LinearGradient(0, 0, 0, 1, [
    {
      offset: 1,
      color: '#F4F4F9',
    },

    {
      offset: 0,
      color: '#E7E9F4',
    },
  ]);

  const series: ChartSeries<{ name: string; value: number }>[] = [];

  const barsIndexes = Array.from({ length: barsCount }, (_, idx) => idx);

  for (const idx of barsIndexes) {
    const data = categories.map((category, index) => ({
      name: category,
      value: generatePlaceholderValue(idx * categories.length + index),
    }));

    series.push({
      name: `Series ${idx + 1}`,
      type: 'bar',
      ...(barWidth && { barWidth }),
      itemStyle: {
        color,
        opacity,
        ...itemStyle,
      },
      data,
    });
  }

  return series;
}
